var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { html, LitElement } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { BaseStyle } from "../../ui/styles.js";
import { RrWindow, WindowDelegate } from "../../ui/controls/window.js";
import "../../ui/controls/frequency-input.js";
import "../../ui/controls/window.js";
const AVAILABLE_SAMPLE_RATES = (() => {
    let rateSet = new Set([256000]);
    for (let r = 1024000; r < 3000000; r += 256000)
        rateSet.add(r);
    for (let r = 960000; r < 3000000; r += 192000)
        rateSet.add(r);
    let rates = [...rateSet];
    rates.sort((a, b) => Number(a) - Number(b));
    return rates;
})();
const AVAILABLE_FFT_SIZES = (() => {
    let sizes = new Array();
    for (let s = 32; s <= 32768; s *= 2) {
        sizes.push(s);
    }
    return sizes;
})();
const LOW_FREQUENCY_METHODS = new Map([
    ["default", "Default method"],
    ["directSampling", "Direct sampling"],
    ["upconverter", "External upconverter"],
]);
const DIRECT_SAMPLING_CHANNELS = new Map([
    ["Q", "Q"],
    ["I", "I"],
]);
const FM_DEEMPH_TCS = new Map([
    [50, "Europe"],
    [75, "USA"],
]);
const PERFORMANCE_TRADEOFFS = new Map([
    ["cpu", "Use more CPU"],
    ["latency", "Have more latency"],
    ["quality", "Have worse quality"],
]);
let RrSettings = class RrSettings extends WindowDelegate(LitElement) {
    constructor() {
        super(...arguments);
        this.inline = false;
        this.playing = false;
        this.sampleRate = 1024000;
        this.ppm = 0;
        this.fftSize = 2048;
        this.fmDeemph = 50;
        this.biasTee = false;
        this.lowFrequencyMethod = {
            name: "default",
            channel: "Q",
            frequency: 100000000,
            biasTee: false,
        };
        this.performanceTradeoff = "cpu";
    }
    static get styles() {
        return [BaseStyle];
    }
    render() {
        return html `<rr-window
      label="Settings"
      id="settings"
      closeable
      class=${this.inline ? "inline" : ""}
      .position=${this.position}
      .fixed=${this.inline}
    >
      <div>
        <label for="sampleRate">Sample rate: </label
        ><select
          id="sampleRate"
          .disabled=${this.playing}
          @change=${this.onSampleRateChange}
        >
          ${AVAILABLE_SAMPLE_RATES.map((r) => html `<option value=${r} .selected=${this.sampleRate == r}>
                ${r}
              </option>`)}
        </select>
      </div>
      <div>
        <label for="ppm">Clock correction: </label
        ><input
          id="ppm"
          type="number"
          min="-500"
          max="500"
          step="1"
          .value=${String(this.ppm)}
          @change=${this.onPpmChange}
        />PPM
      </div>
      <div>
        <label for="fftSize">FFT size: </label
        ><select id="fftSize" @change=${this.onFftSizeChange}>
          ${AVAILABLE_FFT_SIZES.map((s) => html `<option value=${s} .selected=${this.fftSize == s}>
                ${s}
              </option>`)}
        </select>
      </div>
      <div>
        <label for="fmDeemph">WBFM de-emphasis: </label
        ><select
          id="fmDeemph"
          .disabled=${this.playing}
          @change=${this.onFmDeemphChange}
        >
          ${FM_DEEMPH_TCS.entries().map((r) => html `<option value=${r[0]} .selected=${this.fmDeemph == r[0]}>
                ${r[0]}µs &mdash; ${r[1]}
              </option>`)}
        </select>
      </div>
      <div>
        <label for="biasTee">Bias T: </label
        ><input
          type="checkbox"
          id="biasTee"
          .checked=${this.biasTee}
          @change=${this.onBiasTeeChange}
        />
      </div>
      <div>
        <label for="lowFreqMethod">0-29MHz method: </label
        ><select id="lowFreqMethod" @change=${this.onLowFrequencyMethodChange}>
          ${LOW_FREQUENCY_METHODS.entries().map(([k, v]) => html `<option
                value=${String(k)}
                .selected=${this.lowFrequencyMethod.name == k}
              >
                ${v}
              </option>`)}
        </select>
      </div>
      <div .hidden=${this.lowFrequencyMethod.name != "directSampling"}>
        <label for="directSamplingChannel">Direct sampling channel: </label
        ><select
          id="directSamplingChannel"
          @change=${this.onDirectSamplingChannelChange}
        >
          ${DIRECT_SAMPLING_CHANNELS.entries().map(([k, v]) => html `<option
                value=${String(k)}
                .selected=${this.lowFrequencyMethod.channel == k}
              >
                ${v}
              </option>`)}
        </select>
      </div>
      <div .hidden=${this.lowFrequencyMethod.name != "upconverter"}>
        <label for="upconverterFrequency">Upconverter frequency: </label
        ><input
          type="number"
          id="upconverterFrequency"
          min="1"
          max="1800000000"
          step="1"
          .value=${String(this.lowFrequencyMethod.frequency)}
          @change=${this.onUpconverterFrequencyChange}
        />
      </div>
      <div .hidden=${this.lowFrequencyMethod.name != "upconverter"}>
        <label for="upconverterBiasTee">Use bias T for upconverter: </label
        ><input
          type="checkbox"
          id="upconverterBiasTee"
          .checked=${this.lowFrequencyMethod.biasTee}
          @change=${this.onUpconverterBiasTeeChange}
        />
      </div>
      <div>
        <label for="performanceTradeoff">Performance trade-off: </label
        ><select
          id="performanceTradeoff"
          .disabled=${this.playing}
          @change=${this.onPerformanceTradeoffChange}
        >
          ${PERFORMANCE_TRADEOFFS.entries().map((r) => html `<option
                value=${r[0]}
                .selected=${this.performanceTradeoff == r[0]}
              >
                ${r[1]}
              </option>`)}
        </select>
      </div>
    </rr-window>`;
    }
    onSampleRateChange(e) {
        this.sampleRate = Number(e.target.selectedOptions[0].value);
        this.dispatchEvent(new SampleRateChangedEvent());
    }
    onPpmChange(e) {
        let input = e.target;
        let value = Number(input.value);
        if (isNaN(value)) {
            input.value = String(this.ppm);
            return;
        }
        this.ppm = value;
        this.dispatchEvent(new PpmChangedEvent());
    }
    onFftSizeChange(e) {
        this.fftSize = Number(e.target.selectedOptions[0].value);
        this.dispatchEvent(new FftSizeChangedEvent());
    }
    onFmDeemphChange(e) {
        this.fmDeemph = Number(e.target.selectedOptions[0].value);
        this.dispatchEvent(new FmDeemphChangedEvent());
    }
    onBiasTeeChange(e) {
        this.biasTee = e.target.checked;
        this.dispatchEvent(new BiasTeeChangedEvent());
    }
    onLowFrequencyMethodChange(e) {
        let method = { ...this.lowFrequencyMethod };
        method.name = e.target.selectedOptions[0]
            .value;
        this.lowFrequencyMethod = method;
        this.dispatchEvent(new LowFrequencyMethodChangedEvent());
    }
    onDirectSamplingChannelChange(e) {
        let method = { ...this.lowFrequencyMethod };
        method.channel = e.target.selectedOptions[0]
            .value;
        this.lowFrequencyMethod = method;
        this.dispatchEvent(new LowFrequencyMethodChangedEvent());
    }
    onUpconverterFrequencyChange(e) {
        let target = e.target;
        let value = Number(target.value);
        if (isNaN(value)) {
            target.value = String(this.lowFrequencyMethod.frequency);
            return;
        }
        let method = { ...this.lowFrequencyMethod };
        method.frequency = value;
        this.lowFrequencyMethod = method;
        this.dispatchEvent(new LowFrequencyMethodChangedEvent());
    }
    onUpconverterBiasTeeChange(e) {
        let method = { ...this.lowFrequencyMethod };
        method.biasTee = e.target.checked;
        this.lowFrequencyMethod = method;
        this.dispatchEvent(new LowFrequencyMethodChangedEvent());
    }
    onPerformanceTradeoffChange(e) {
        this.performanceTradeoff = e.target.selectedOptions[0].value;
        this.dispatchEvent(new PerformanceTradeoffChangedEvent());
    }
};
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Boolean)
], RrSettings.prototype, "inline", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Boolean)
], RrSettings.prototype, "playing", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrSettings.prototype, "sampleRate", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrSettings.prototype, "ppm", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrSettings.prototype, "fftSize", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrSettings.prototype, "fmDeemph", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Boolean)
], RrSettings.prototype, "biasTee", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Object)
], RrSettings.prototype, "lowFrequencyMethod", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", String)
], RrSettings.prototype, "performanceTradeoff", void 0);
__decorate([
    query("rr-window"),
    __metadata("design:type", typeof (_a = typeof RrWindow !== "undefined" && RrWindow) === "function" ? _a : Object)
], RrSettings.prototype, "window", void 0);
RrSettings = __decorate([
    customElement("rr-settings")
], RrSettings);
export { RrSettings };
class SampleRateChangedEvent extends Event {
    constructor() {
        super("rr-sample-rate-changed", { bubbles: true, composed: true });
    }
}
class PpmChangedEvent extends Event {
    constructor() {
        super("rr-ppm-changed", { bubbles: true, composed: true });
    }
}
class FftSizeChangedEvent extends Event {
    constructor() {
        super("rr-fft-size-changed", { bubbles: true, composed: true });
    }
}
class FmDeemphChangedEvent extends Event {
    constructor() {
        super("rr-fm-deemph-changed", { bubbles: true, composed: true });
    }
}
class BiasTeeChangedEvent extends Event {
    constructor() {
        super("rr-bias-tee-changed", { bubbles: true, composed: true });
    }
}
class LowFrequencyMethodChangedEvent extends Event {
    constructor() {
        super("rr-low-frequency-method-changed", { bubbles: true, composed: true });
    }
}
class PerformanceTradeoffChangedEvent extends Event {
    constructor() {
        super("rr-performance-tradeoff-changed", { bubbles: true, composed: true });
    }
}
